import React, { useEffect, useMemo, useState } from "react"
import { useSelector } from "react-redux"
import clsx from "clsx"

import { LazyImage } from "components"
import { getFoods } from "services"
import {
  refactorFoods,
  resolveStorageUrl,
  thousandSeparator
} from "utils"
import {
  getStoreDesignStyle,
  mergeStoreDesign
} from "utils/storeDesign"
import PotatoTImeWinter from "./PotatoTImeWinter"
import classes from "./KioskLandingRenderer.module.scss"

const changeYouTubeUrl = (url = "") =>
  url.replace("/watch?v=", "/embed/")

const getShellStyle = component => ({
  ...(component && component.backgroundColor
    ? { backgroundColor: component.backgroundColor }
    : {}),
  textAlign: (component && component.contentAlign) || "right"
})

const renderComponentBackground = component => {
  if (!component?.backgroundMediaUrl) return null

  const style = {
    opacity: Number(component.backgroundOpacity ?? 1)
  }
  const src = resolveStorageUrl(component.backgroundMediaUrl)

  return component.backgroundMediaType === "video"
    ? <video
        className={classes["component-background-media"]}
        src={src}
        style={style}
        autoPlay
        muted
        loop
        playsInline
        controls={false}
      />
    : <LazyImage
        className={classes["component-background-media"]}
        src={src}
        alt=""
      />
}

const floatingButtonShapeRadius = {
  rectangle: 8,
  rounded: 999,
  circle: 999,
  pill: 999
}

const responsiveDeviceKeys = ["desktop", "tablet", "mobile"]

const clamp = (value, min, max) =>
  Math.min(Math.max(Number(value) || min, min), max)

const getResponsiveConfig = (component, device) => ({
  position: component.position,
  width: component.width,
  height: component.height,
  ...(component.responsive?.[device] || {})
})

const getResponsiveFloatingButtonPositionStyle = component =>
  responsiveDeviceKeys.reduce((style, device) => {
    const config = getResponsiveConfig(component, device)
    const position = config.position || {}

    return {
      ...style,
      [`--floating-button-${device}-left`]: `${clamp(position.x ?? 72, 0, 100)}%`,
      [`--floating-button-${device}-top`]: `${clamp(position.y ?? 72, 0, 100)}%`
    }
  }, {})

const getFloatingButtonStyle = component => {
  const width = Number(component.width) || 160
  const height = Number(component.height) || 56
  const shape = component.shape || "rounded"

  return {
    ...getResponsiveFloatingButtonPositionStyle(component),
    width: shape === "circle" ? `${height}px` : `${width}px`,
    height: `${height}px`,
    borderRadius: `${floatingButtonShapeRadius[shape] ?? 8}px`,
    backgroundColor: component.buttonColor || "#111827",
    color: component.textColor || "#FFFFFF",
    fontSize: `${Number(component.fontSize) || 16}px`
  }
}

const getFloatingVideoStyle = component => {
  const responsiveStyle =
    responsiveDeviceKeys.reduce((style, device) => {
      const config = getResponsiveConfig(component, device)
      const position = config.position || {}

      return {
        ...style,
        [`--floating-video-${device}-left`]: `${clamp(position.x ?? 72, 0, 100)}%`,
        [`--floating-video-${device}-top`]: `${clamp(position.y ?? 72, 0, 100)}%`,
        [`--floating-video-${device}-width`]: `${clamp(config.width ?? 32, 12, 100)}%`,
        [`--floating-video-${device}-height`]: `${clamp(config.height ?? 24, 8, 100)}%`
      }
    }, {})

  return {
    ...responsiveStyle,
    "--floating-video-fit": component.objectFit || "cover"
  }
}

const KioskLandingFloatingButton = ({ component }) => (
  <button
    type="button"
    className={classes["floating-button"]}
    style={getFloatingButtonStyle(component)}
  >
    {component.text || "شروع سفارش"}
  </button>
)

const KioskLandingFloatingVideo = ({ component }) => {
  if (!component.url) return null

  return (
    <div
      className={classes["floating-video"]}
      style={getFloatingVideoStyle(component)}
    >
      <video
        className={classes["floating-video-media"]}
        src={resolveStorageUrl(component.url)}
        autoPlay
        muted
        loop
        playsInline
        controls={false}
      />
    </div>
  )
}

const KioskLandingResizableVideo = ({ component }) => {
  if (!component.url) return null
  const textOverlays =
    Array.isArray(component.textOverlays)
      ? component.textOverlays
      : []

  return (
    <div
      className={classes["resizable-video"]}
      style={{ minHeight: `${clamp(component.videoHeight ?? 280, 90, 900)}px` }}
    >
      <div
        className={classes["resizable-video-frame"]}
        style={{
          width: `${clamp(component.videoWidth ?? 100, 20, 100)}%`,
          height: `${clamp(component.videoHeight ?? 280, 90, 900)}px`
        }}
      >
        <video
          className={classes["resizable-video-media"]}
          src={resolveStorageUrl(component.url)}
          autoPlay
          muted
          loop
          playsInline
          controls={false}
        />
        {textOverlays.map(item =>
          <div
            key={item.id}
            className={classes["resizable-video-text"]}
            style={{
              left: `${clamp(item.x ?? 50, 0, 100)}%`,
              top: `${clamp(item.y ?? 50, 0, 100)}%`,
              color: item.color || "#FFFFFF",
              backgroundColor: item.backgroundColor || "rgba(0, 0, 0, 0.35)",
              fontSize: `${clamp(item.fontSize ?? 24, 10, 96)}px`
            }}
          >
            {item.text}
          </div>
        )}
      </div>
    </div>
  )
}

const KioskLandingCarousel = ({ component }) => {
  const images =
    Array.isArray(component.images)
      ? component.images.filter(Boolean)
      : []

  const [activeIndex, setActiveIndex] = useState(0)

  useEffect(() => {
    if (images.length <= 1) return undefined

    const interval =
      setInterval(() => {
        setActiveIndex(prev => (prev + 1) % images.length)
      }, 4500)

    return () => clearInterval(interval)
  }, [images.length])

  if (images.length === 0) return null

  return (
    <div className={classes["carousel"]}>
      {images.map((image, index) =>
        <div
          key={`${image}-${index}`}
          className={clsx(
            classes["carousel-slide"],
            index === activeIndex && classes["carousel-slide-active"]
          )}
        >
          <LazyImage
            src={image}
            alt=""
            className={classes["carousel-image"]}
          />
        </div>
      )}
      {images.length > 1 &&
        <div className={classes["carousel-dots"]}>
          {images.map((image, index) =>
            <span
              key={`${image}-dot-${index}`}
              className={clsx(
                classes["carousel-dot"],
                index === activeIndex && classes["carousel-dot-active"]
              )}
            />
          )}
        </div>
      }
    </div>
  )
}

const KioskLandingSingleImage = ({ component }) => {
  if (!component.image) return null

  const isFullPageImage =
    component.type === "fullPageImage"

  return (
    <div
      className={classes["single-image"]}
      data-full-page={isFullPageImage ? "true" : "false"}
    >
      <LazyImage
        src={component.image}
        alt=""
        className={classes["single-image-media"]}
        wrapperProps={isFullPageImage ? { style: { display: "block" } } : undefined}
      />
    </div>
  )
}

const KioskLandingDoubleImages = ({ component }) => {
  const images =
    Array.isArray(component.images)
      ? component.images.filter(Boolean)
      : []

  if (images.length === 0) return null

  return (
    <div className={classes["double-images"]}>
      {images.slice(0, 2).map((image, index) =>
        <div key={`${image}-${index}`} className={classes["double-image-card"]}>
          <LazyImage
            src={image}
            alt=""
            className={classes["double-image-media"]}
          />
        </div>
      )}
    </div>
  )
}

const KioskLandingDescription = ({ component }) => (
  <div
    className={clsx(classes["rich-text"], "ql-editor")}
    dangerouslySetInnerHTML={{
      __html: component.description || ""
    }}
  />
)

const KioskLandingVideo = ({ component }) => {
  if (!component.url) return null

  const aspectRatio =
    Number(component.aspectRatio) || (16 / 9)

  return (
    <div className={classes["video-shell"]}>
      <div
        className={classes["video-ratio"]}
        style={{ paddingTop: `${(1 / aspectRatio) * 100}%` }}
      />
      <iframe
        title="kiosk-landing-video"
        className={classes["video-frame"]}
        src={changeYouTubeUrl(component.url)}
        allowFullScreen
      />
    </div>
  )
}

const KioskLandingFullPageVideo = ({ component }) => {
  if (!component.url) return null

  return (
    <div className={classes["full-page-video"]}>
      <video
        className={classes["full-page-video-media"]}
        src={resolveStorageUrl(component.url)}
        autoPlay
        muted
        loop
        playsInline
      />
    </div>
  )
}

const KioskLandingHtml = ({ component }) => {
  const [height, setHeight] = useState(0)

  return (
    <iframe
      scrolling="no"
      height={`${height}px`}
      className={classes["html-frame"]}
      srcDoc={component.html || "<section></section>"}
      onLoad={event => {
        const nextHeight =
          event.target.contentWindow.document.body.scrollHeight

        setHeight(nextHeight)
      }}
      title="kiosk-landing-html"
    />
  )
}

const KioskLandingSocialLinks = ({ component }) => {
  const links = [
    ["Instagram", component.instagramUrl],
    ["WhatsApp", component.whatsappUrl],
    ["Telegram", component.telegramUrl],
    ["Email", component.emailUrl]
  ].filter(([, url]) => !!url)

  if (links.length === 0) return null

  return (
    <div className={classes["social-links"]}>
      {links.map(([label, url]) =>
        <a
          key={label}
          href={url}
          onClick={event => event.preventDefault()}
          className={classes["social-link"]}
        >
          {label}
        </a>
      )}
    </div>
  )
}

const KioskLandingProductCard = ({ food, design }) => {
  const option = food.options[0]

  if (!option) return null

  const isSoldout =
    food.options.every(currentOption =>
      currentOption.outOfStock ||
      (
        currentOption.stockManagement &&
        currentOption.availableQuantity <= 0
      )
    )

  const cardDecoration =
    design.productCard.decoration || {}

  const cardDecorationImage =
    cardDecoration.enabled && cardDecoration.image
      ? resolveStorageUrl(cardDecoration.image)
      : ""

  const cardDecorationPosition =
    cardDecoration.position || {}

  return (
    <article
      className={classes["product-card"]}
      style={{
        "--product-card-radius": `${design.productCard.radius}px`,
        "--product-card-image-fit": design.productCard.imageFit
      }}
      data-card-variant={design.productCard.variant}
    >
      <div className={classes["product-image-shell"]}>
        {!!cardDecorationImage &&
          <img
            src={cardDecorationImage}
            alt=""
            className={classes["card-decoration"]}
            data-animation={cardDecoration.animation || "shake"}
            style={{
              "--decoration-x": `${cardDecorationPosition.x ?? 12}%`,
              "--decoration-y": `${cardDecorationPosition.y ?? 0}%`
            }}
          />
        }

        <LazyImage
          src={food.imageCover}
          alt={food.name}
          className={classes["product-image"]}
        />
        {food.isNewFlag &&
          <span className={clsx(classes["product-badge"], classes["badge-new"])}>
            جدید
          </span>
        }
        {food.isFeaturedFlag &&
          <span className={clsx(classes["product-badge"], classes["badge-featured"])}>
            ویژه
          </span>
        }
        {isSoldout &&
          <div className={classes["soldout"]}>
            ناموجود
          </div>
        }
      </div>
      <div className={classes["product-body"]}>
        <strong>{food.name}</strong>
        {food.description &&
          <p>{food.description}</p>
        }
        <div className={classes["product-price-row"]}>
          {option.price > option.salePrice &&
            <span className={classes["product-old-price"]}>
              {thousandSeparator(option.price)}
            </span>
          }
          <span className={classes["product-price"]}>
            {thousandSeparator(option.salePrice)}
          </span>
        </div>
      </div>
    </article>
  )
}

const KioskLandingProductHighlights = ({ component, design }) => {
  const selectedCashierBranch =
    useSelector(state => state.userAccountInfo.selectedCashierBranch)

  const branchId = selectedCashierBranch && selectedCashierBranch._id

  const [foods, setFoods] = useState(null)

  useEffect(() => {
    const getProductHighlights = async () => {
      if (!branchId) return

      const mode = component.mode || "selected"
      const foodIds = component.foodIds || []

      if (mode === "selected" && foodIds.length === 0) {
        setFoods([])
        return
      }

      try {
        const queries =
          mode === "featured"
            ? {
              isAvailable: true,
              onlyForAccess: false,
              branchId,
              isFeaturedFlag: true,
              limit: component.limit || 12
            }
            : mode === "latest"
              ? {
                isAvailable: true,
                onlyForAccess: false,
                branchId,
                limit: component.limit || 12
              }
              : {
                isAvailable: true,
                onlyForAccess: false,
                branchId,
                limit: foodIds.length,
                foodIds
              }

        const response =
          await getFoods(queries)

        setFoods(
          refactorFoods(response, branchId)
        )
      } catch (err) {
        console.log(err)
        setFoods([])
      }
    }

    getProductHighlights()
  }, [branchId, component])

  const columns =
    `repeat(${design.productGrid.desktopColumns}, minmax(0, 1fr))`

  if (foods === null) {
    return <div className={classes["placeholder"]} />
  }

  if (foods.length === 0) {
    return null
  }

  return (
    <div
      className={classes["products-grid"]}
      style={{ gridTemplateColumns: columns }}
    >
      {foods.map(food =>
        <KioskLandingProductCard
          key={food._id}
          food={food}
          design={design}
        />
      )}
    </div>
  )
}

const KioskLandingLayoutGrid = props => {
  const {
    component,
    design,
    renderComponent
  } = props

  return (
    <div
      className={classes["layout-grid"]}
      style={{ gridTemplateColumns: `repeat(${component.columns || 3}, minmax(0, 1fr))` }}
    >
      {(component.cards || []).map(card =>
        <section
          key={card.id}
          className={classes["layout-card"]}
          data-variant={component.variant || "elevated"}
        >
          {card.title &&
            <h3>{card.title}</h3>
          }
          <div className={classes["layout-card-components"]}>
            {(card.components || []).map(nestedComponent =>
              <div
                key={nestedComponent.id}
                className={classes["layout-card-component-shell"]}
                style={getShellStyle(nestedComponent)}
              >
                {renderComponentBackground(nestedComponent)}
                <div className={classes["component-content"]}>
                  {renderComponent(nestedComponent, design)}
                </div>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  )
}

const renderComponent = (component, design) => {
  if (!component) return null

  if (component.type === "carousel") {
    return <KioskLandingCarousel component={component} />
  }

  if (component.type === "singleImage") {
    return <KioskLandingSingleImage component={component} />
  }

  if (component.type === "fullPageImage") {
    return <KioskLandingSingleImage component={component} />
  }

  if (component.type === "doubleImages") {
    return <KioskLandingDoubleImages component={component} />
  }

  if (component.type === "description") {
    return <KioskLandingDescription component={component} />
  }

  if (component.type === "video") {
    return <KioskLandingVideo component={component} />
  }

  if (component.type === "resizableVideo") {
    return <KioskLandingResizableVideo component={component} />
  }

  if (component.type === "fullPageVideo") {
    return <KioskLandingFullPageVideo component={component} />
  }

  if (component.type === "html") {
    return <KioskLandingHtml component={component} />
  }

  if (component.type === "link") {
    return <KioskLandingSocialLinks component={component} />
  }

  if (component.type === "productHighlights") {
    return (
      <KioskLandingProductHighlights
        component={component}
        design={design}
      />
    )
  }

  if (component.type === "potatoTimeShowcase") {
    return <PotatoTImeWinter />
  }

  if (component.type === "layoutGrid") {
    return (
      <KioskLandingLayoutGrid
        component={component}
        design={design}
        renderComponent={renderComponent}
      />
    )
  }

  if (component.type === "floatingButton") {
    return <KioskLandingFloatingButton component={component} />
  }

  if (component.type === "floatingVideo") {
    return <KioskLandingFloatingVideo component={component} />
  }

  return null
}

const KioskLandingRenderer = ({ landingLayout }) => {
  const normalizedDesign =
    useMemo(() => mergeStoreDesign(landingLayout.design), [landingLayout.design])

  const components =
    Array.isArray(landingLayout.components)
      ? landingLayout.components
      : []
  const floatingComponents =
    components.filter(component =>
      ["floatingButton", "floatingVideo"].includes(component.type)
    )
  const contentComponents =
    components.filter(component =>
      !["floatingButton", "floatingVideo"].includes(component.type)
    )
  const hasFullPageVideo =
    contentComponents.some(component => component.type === "fullPageVideo")

  return (
    <div
      className={classes["container"]}
      style={getStoreDesignStyle(normalizedDesign)}
      data-template={normalizedDesign.templateKey}
      data-full-page-video={hasFullPageVideo ? "true" : "false"}
    >
      <div className={classes["content"]}>
        {contentComponents.map(component =>
          <section
            key={component.id || component.type}
            className={classes["component-section"]}
          >
            <div
              className={classes["component-shell"]}
              style={getShellStyle(component)}
            >
              {renderComponentBackground(component)}
              <div className={classes["component-content"]}>
                {renderComponent(component, normalizedDesign)}
              </div>
            </div>
          </section>
        )}
      </div>
      {floatingComponents.length > 0 &&
        <div className={classes["floating-layer"]}>
          {floatingComponents.map(component =>
            component.type === "floatingVideo"
              ? <KioskLandingFloatingVideo
                  key={component.id || component.type}
                  component={component}
                />
              : <KioskLandingFloatingButton
              key={component.id || component.type}
              component={component}
            />
          )}
        </div>
      }
      {/* <div className={classes["cta"]}>
        برای شروع سفارش صفحه را لمس کنید
      </div> */}
    </div>
  )
}

export default KioskLandingRenderer
